// Runtime i18n — 1 instance Lingui dùng chung cho toàn app.
//
// ══ VÌ SAO THIẾT KẾ NHƯ THẾ NÀY (đọc trước khi sửa) ══
//
// Spike 2026-08-08 đo thật trên Lingui 6.6.0:
//   • Bản production babel STRIP sạch chữ tiếng Việt khỏi component, chỉ còn `{id:"JKhSNT"}`.
//     ⇒ KHÔNG có chuyện "tiếng Việt nằm inline nên khỏi nạp catalog". Thiếu catalog là
//     màn hình lòi ra chuỗi băm "JKhSNT". Catalog vi BẮT BUỘC phải nạp.
//   • Bản dev thì GIỮ `message` inline ⇒ chạy dev không cần compile catalog.
//
// Nên chia catalog theo CỤM (mỗi thư mục src/pages/* là 1 cụm), và:
//   • vi  → import TĨNH trong shim cụm ⇒ Rollup gói thẳng vào lazy chunk của page đó.
//           User Việt: 0 request thêm, số byte gần như không đổi (chữ chỉ chuyển chỗ
//           từ code sang catalog trong CÙNG chunk).
//   • !vi → dynamic import ⇒ chỉ user ngôn ngữ đó mới tải, và chỉ cụm họ đang mở.
//
// Chống nháy chữ: shim cụm chạy lúc module-eval của lazy chunk, TRƯỚC khi React render
// page. Nó tăng `pending` rồi mới bắt đầu tải. Hai lớp chờ:
//   • Lần vẽ ĐẦU của app: Provider thấy pending>0 → hiện fallback. Xem I18nProvider.jsx.
//   • Mọi trang/drawer lazy SAU đó: `lazyWithReload` chờ `whenCatalogsLoaded()` rồi mới trả
//     module cho React ⇒ trang không bao giờ vẽ khi catalog cụm của nó còn đang về. Xem
//     whenCatalogsLoaded bên dưới — vì sao không thể trông vào «catalog về thì Lingui vẽ lại».

import { setupI18n } from '@lingui/core';
import { LOCALE_CODES, SOURCE_LOCALE, normalizeLocale, isSupportedLocale } from './locales.js';
import { setDateOrder } from './dateOrder.js';
import { setTaskTimeOrder } from './taskTimeOrder.js';

const STORAGE_KEY = 'cnv.locale';

export const i18n = setupI18n();

// ── Trạng thái nội bộ ────────────────────────────────────────────────────────
let enabledLocales = [...LOCALE_CODES];
let currentLocale = SOURCE_LOCALE;
let pending = 0;
const listeners = new Set();

/** Mỗi cụm đã đăng ký: name → { load(locale), loaded: Set<locale>, source: catalog vi } */
const clusters = new Map();
/** Khoá ĐÃ NẠP của từng ngôn ngữ khác nguồn: locale → Set<id>. Để lọc bản chưa dịch, xem locChuaDich. */
const daNap = new Map();
/** Lượt nạp catalog đang bay (Promise của ensureCluster, không bao giờ reject). Xem whenCatalogsLoaded. */
const dangNap = new Set();

function notify() {
  for (const fn of listeners) fn();
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getLocale() {
  return currentLocale;
}

export function getPendingCount() {
  return pending;
}

export function getEnabledLocales() {
  return [...enabledLocales];
}

// ── Chọn ngôn ngữ ban đầu ────────────────────────────────────────────────────
//
// Đọc ĐỒNG BỘ (localStorage) để chốt xong trước lần vẽ đầu tiên — không có cảnh
// hiện tiếng Việt rồi mới nháy sang tiếng Anh.
//
// Thứ tự: đã lưu → ngôn ngữ trình duyệt → tiếng Việt.
// Hồ sơ user trên server (meta.users.locale) KHÔNG nằm ở đây vì lúc này chưa gọi API;
// host gọi setLocale() sau khi login xong (xem applyServerLocale).
function readStoredLocale() {
  try {
    return normalizeLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null; // Safari private mode / storage bị chặn
  }
}

// `allowed`: chỉ nhận ngôn ngữ HỆ ĐANG BẬT. Trước 12/10/2026 lấy ngôn ngữ đầu tiên kernel biết,
// kể cả khi hệ không bật ⇒ trình duyệt ['es-MX','es','en-US'] ra 'es' (Site Connect chưa bật es)
// rồi rơi về enabledLocales[0] = 'vi' — khách Mỹ Latinh mở trang đăng nhập Site Connect thấy tiếng Việt.
function readBrowserLocale(allowed) {
  try {
    for (const l of navigator.languages || [navigator.language]) {
      const n = normalizeLocale(l);
      if (n && (!allowed || allowed.includes(n))) return n;
    }
  } catch { /* SSR hoặc môi trường không có navigator */ }
  return null;
}

function persistLocale(code) {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch { /* không lưu được thì thôi, phiên sau về mặc định */ }
}

/**
 * Host gọi 1 lần lúc boot, TRƯỚC khi render.
 *
 * @param {string[]} [opts.enabled] - ngôn ngữ host này cho phép. Core truyền ['vi']
 *   để khoá tiếng Việt (User model của Core chưa có cột locale — xem docs/I18N.md).
 * @param {string}  [opts.locale]   - ép cứng 1 ngôn ngữ (bỏ qua localStorage/trình duyệt).
 * @param {'mdy'|'dmy'} [opts.dateOrder] - chuẩn viết ngày bằng số của HỆ (dateOrder.js): Site Connect
 *   'mdy' (mm/dd/yyyy); không truyền ⇒ 'dmy' (Work, Core). Không đổi theo ngôn ngữ.
 * @param {'start_first'|'due_first'} [opts.taskTimeOrder] - hàng «Bắt đầu» hay «Hạn» đứng trên trong ô thời gian của việc
 *   (taskTimeOrder.js): Site Connect 'start_first'; không truyền ⇒ 'due_first' (Work, Core).
 * @param {string}  [opts.fallback] - ngôn ngữ khi chưa lưu gì và trình duyệt không khớp ngôn ngữ nào đã bật
 *   (vd trình duyệt tiếng Hoa). Site Connect 'en'; không truyền ⇒ tiếng Việt như trước (Work, Core).
 */
export function initI18n({ enabled, locale, dateOrder, taskTimeOrder, fallback } = {}) {
  if (dateOrder) setDateOrder(dateOrder);
  if (taskTimeOrder) setTaskTimeOrder(taskTimeOrder);
  if (Array.isArray(enabled) && enabled.length) {
    enabledLocales = enabled.filter(isSupportedLocale);
    if (!enabledLocales.length) enabledLocales = [SOURCE_LOCALE];
  }
  const fb = normalizeLocale(fallback);
  const fallbackLocale = fb && enabledLocales.includes(fb) ? fb : null;
  const wanted = normalizeLocale(locale) || readStoredLocale() || readBrowserLocale(enabledLocales)
    || fallbackLocale || SOURCE_LOCALE;
  currentLocale = enabledLocales.includes(wanted) ? wanted : (fallbackLocale || enabledLocales[0]);
  i18n.activate(currentLocale);
  // ⚠️ SỰ CỐ PROD 2026-08-08 (hash id lòi toàn trang login): trong bundle production,
  // shim cụm (import TĨNH) eval TRƯỚC main.jsx body — registerCluster chạy khi
  // currentLocale còn là 'vi' mặc định nên chỉ nạp catalog vi. initI18n chốt locale
  // khác xong mà không nạp lại → activate('en') với messages rỗng → mọi chuỗi trả id.
  // Dev không lộ (module eval theo request + message inline). Nên: sau khi chốt locale,
  // nạp catalog locale đó cho MỌI cụm đã đăng ký trước mình. I18nProvider gate pending
  // che quá trình tải — không nháy chữ.
  if (currentLocale !== SOURCE_LOCALE) {
    for (const entry of clusters.values()) ensureCluster(entry, currentLocale);
  }
  return currentLocale;
}

/**
 * Đăng ký catalog của 1 cụm. Shim sinh tự động ở src/i18n/c/<cụm>.js gọi hàm này
 * lúc module-eval — tức là TRƯỚC khi React render page của cụm đó.
 *
 * @param {string} name              - tên cụm (= tên thư mục src/pages/<name>)
 * @param {object} sourceMessages    - catalog vi đã compile, import TĨNH
 * @param {(locale:string)=>Promise} load - dynamic import catalog ngôn ngữ khác
 */
export function registerCluster(name, sourceMessages, load) {
  if (clusters.has(name)) return;
  // ⚠️ Dùng dạng OBJECT {locale: messages} — dạng 2 tham số load('vi', msgs) trên
  // Lingui 6.6 no-op IM LẶNG (đã dính thật 2026-08-08: i18n.messages rỗng, chữ vẫn
  // hiện nhờ message inline của dev build, sang production là lòi hash id).
  i18n.load({ [SOURCE_LOCALE]: sourceMessages });
  const entry = { load, loaded: new Set([SOURCE_LOCALE]), source: sourceMessages };
  clusters.set(name, entry);
  if (currentLocale !== SOURCE_LOCALE) ensureCluster(entry, currentLocale);
}

function ensureCluster(entry, locale) {
  if (entry.loaded.has(locale)) return Promise.resolve();
  entry.loaded.add(locale); // đánh dấu TRƯỚC để 2 page cùng cụm không tải đôi
  pending += 1;
  notify();
  const p = Promise.resolve()
    .then(() => entry.load(locale))
    .then((mod) => {
      const daCo = daNap.get(locale) || new Set();
      const messages = locale === SOURCE_LOCALE ? mod.messages : locChuaDich(mod.messages, entry.source, daCo);
      i18n.load({ [locale]: messages });
      for (const id of Object.keys(messages)) daCo.add(id);
      daNap.set(locale, daCo);
      // activate lại để Lingui phát sự kiện change → component vẽ lại bằng chữ mới
      if (getLocale() === locale) i18n.activate(locale);
    })
    .catch((err) => {
      entry.loaded.delete(locale);
      console.warn(`[i18n] không tải được catalog "${locale}" cho cụm này:`, err?.message || err);
      // Production strip message inline → thiếu catalog là chuỗi render RA HASH ID
      // (đã kiểm chứng: fallbackLocales của Lingui KHÔNG cứu missing key). Thà rơi cả
      // app về tiếng Việt còn hơn cho user nhìn mã băm. localStorage GIỮ lựa chọn cũ —
      // reload/lần sau sẽ thử tải lại.
      if (currentLocale === locale && locale !== SOURCE_LOCALE) {
        currentLocale = SOURCE_LOCALE;
        i18n.activate(SOURCE_LOCALE);
      }
    })
    .finally(() => {
      pending -= 1;
      dangNap.delete(p);
      notify();
    });
  dangNap.add(p);
  return p;
}

/**
 * CHỜ CATALOG ĐANG NẠP DỞ — gọi TRƯỚC khi vẽ một trang/drawer vừa tải chunk.
 *
 * Sự cố 12/10/2026 (Site Connect bản EN, mở thẳng /me): cột trái «Personal settings» hiện
 * `uPOt2g / x3n4Gs / AGNQrT / cdsBI4 / 07ORnH / 8fbAVB` thay vì chữ. Đo trên bản build
 * production: chunk MeSettingsPage về ở 116ms, shim của nó mới BẮT ĐẦU tải catalog en
 * `pages-me` (về ở 172ms) — React vẽ trang ngay giữa hai mốc đó, khi bộ nhớ Lingui chưa có
 * khoá nào của cụm ⇒ bản production (đã strip chữ inline) trả về MÃ BĂM.
 *
 * Catalog về sau đó thì có, nhưng KHÔNG cứu được: `i18n.load` + `activate` chỉ làm vẽ lại
 * các thành phần ĐĂNG KÝ với Lingui (`<Trans>`, `useLingui`). Mẫu chuẩn của docs/I18N.md cho
 * hằng ngoài component — `` msg`…` `` + `i18n._(X.k)` / `tLabel` / `tMeta` / `tOptions` lúc
 * render — đọc thẳng instance toàn cục, nên component đó giữ nguyên mã băm tới lần vẽ lại kế
 * tiếp (ở /me: phải bấm sang tab khác mới hiện chữ). Gate của I18nProvider chỉ che LẦN VẼ ĐẦU
 * (bản vá «web tự tải lại» 16/08 — tháo cây mỗi lần một cụm nạp là mount lại cả trang).
 *
 * Nên chặn ở đúng cửa: chunk đã eval xong (shim đã đăng ký + bắt đầu tải) mà catalog chưa về
 * thì CHƯA trả module cho React — Suspense của chính route đó hiện vòng xoay, cây ngoài không
 * bị tháo. `lazyWithReload` gọi hàm này. Chỉ đợi các lượt ĐANG bay lúc gọi (không đợi lượt
 * mở sau), lượt hỏng cũng tính là xong (ensureCluster tự rơi về tiếng Việt).
 * Trả `null` khi không có gì đang nạp — người dùng tiếng Việt (catalog tĩnh) luôn rơi vào đây.
 *
 * Khoá bằng scripts/check-i18n-late-catalog.mjs.
 */
export function whenCatalogsLoaded() {
  if (!dangNap.size) return null;
  return Promise.all([...dangNap]).then(() => undefined);
}

/**
 * LỌC BẢN «CHƯA DỊCH» ĐÈ BẢN SỐNG.
 *
 * Sự cố 19/09/2026 (ảnh sếp Phú): màn tiếng Anh của bàn Xếp việc lòi «Bắt đầu / Kết thúc /
 * 9:30 giờ». Gốc: `lingui compile` gặp msgstr RỖNG thì ĐỔ CHUỖI NGUỒN (tiếng Việt) vào catalog
 * ngôn ngữ đó — catalog en của pages-utilities có `l3gFoV: ["Bắt đầu"]`, y hệt catalog vi. Mọi
 * cụm nạp chung MỘT bộ nhớ Lingui theo khoá băm(msgid + context), và `i18n.load` GỘP bằng
 * Object.assign ⇒ cụm nạp SAU đè cụm nạp TRƯỚC: pages-projects có "Start" hẳn hoi, prefetch nền
 * nạp pages-utilities sau là "Start" thành "Bắt đầu". Thứ tự nạp tuỳ máy, tuỳ trang mở trước —
 * không log nào ghi lại, và catalog nào cũng còn vài trăm khoá chưa dịch nên hễ cụm nào dịch
 * xong cũng vẫn bị cụm hàng xóm đè.
 *
 * Luật: một khoá của cụm mới mà giá trị BẰNG ĐÚNG giá trị nguồn vi của chính cụm đó (= chưa
 * dịch) VÀ ngôn ngữ này ĐÃ có khoá đó từ cụm khác ⇒ bỏ, giữ bản đang sống. Chiều ngược (cụm
 * chưa dịch nạp trước, cụm có bản dịch nạp sau) vốn đã đúng: bản dịch khác nguồn nên được nạp
 * và đè lên. Cụm vi (nguồn) không qua lọc — với vi, "bằng nguồn" chính là đúng. Tự dò khoá đã
 * nạp bằng `daNap` thay vì `i18n.messages`: getter đó chỉ trả bộ của ngôn ngữ ĐANG active,
 * còn `_messages` là trường riêng của Lingui.
 *
 * Trả về CHÍNH object cũ khi không có gì phải bỏ. Khoá bằng scripts/check-i18n-runtime-shadow.mjs.
 */
export function locChuaDich(messages, source, daCo) {
  if (!messages || !source || !daCo || !daCo.size) return messages;
  let out = null;
  for (const id of Object.keys(messages)) {
    if (!daCo.has(id)) continue;
    const goc = source[id];
    if (goc === undefined || !bangNhau(messages[id], goc)) continue;
    if (!out) out = { ...messages };
    delete out[id];
  }
  return out || messages;
}

/** Bản compile là CHUỖI hoặc MẢNG token (chuỗi / ["tên biến"] / ["tên", "kiểu", …]) — so sâu. */
function bangNhau(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) if (!bangNhau(a[i], b[i])) return false;
    return true;
  }
  return false;
}

/**
 * Đổi ngôn ngữ hiển thị. Tải catalog của MỌI cụm đã đăng ký rồi mới activate,
 * để không có cảnh nửa màn hình tiếng Anh nửa tiếng Việt.
 */
export async function setLocale(code) {
  const next = normalizeLocale(code);
  if (!next || !enabledLocales.includes(next) || next === currentLocale) return currentLocale;
  currentLocale = next;
  persistLocale(next);
  await Promise.all([...clusters.values()].map((e) => ensureCluster(e, next)));
  // Tải hụt → ensureCluster đã revert currentLocale về SOURCE_LOCALE; đừng activate đè.
  if (currentLocale === next) i18n.activate(next);
  notify();
  return currentLocale;
}

/**
 * Áp ngôn ngữ lấy từ hồ sơ user trên server (meta.users.locale) sau khi login.
 * Chỉ áp khi user CHƯA tự chọn trên máy này — lựa chọn tại chỗ luôn thắng, để người
 * dùng đổi ở màn login không bị server đạp lại ngay sau khi đăng nhập.
 */
export function applyServerLocale(code) {
  if (readStoredLocale()) return currentLocale;
  return setLocale(code);
}

// ── tMeta: dịch nhãn NẰM TRONG object cấu hình ───────────────────────────────
//
// ⚠️ ĐỌC TRƯỚC KHI SỬA — đây là bản vá cho một lớp lỗi ĐÃ GIẾT UI CÂM:
// Codemod i18n pass 3 bọc nhầm CẢ CỤM cấu hình thay vì chỉ nhãn bên trong:
//     const meta = i18n._(TYPE_META[k]);   // ← SAI
// `i18n._()` nhận MessageDescriptor. Đưa vào một object thường thì nó đọc
// `.id` = undefined → trả về CHUỖI RỖNG "" chứ không throw. Hậu quả câm như hến:
//   • `meta.color` / `meta.key` / `meta.Icon` → undefined  (mất màu, ghi sai field)
//   • `if (!meta) return null`               → rơi HẾT item, danh sách rỗng
//   • `<Icon/>` với Icon=undefined           → React "Element type is invalid" → trắng trang
// Sự cố thật: ô "Liên kết CRM" ở form tạo ticket không hiện nổi 1 kết quả nào.
//
// Cách dùng ĐÚNG cho map dạng `{ key: { label: msg`…`, color, Icon } }`:
//     const meta = tMeta(TYPE_META[k]);   // giữ nguyên object, chỉ dịch nhãn
// Chỉ cần `i18n._()` khi đọc THẲNG một descriptor: `i18n._(TYPE_META[k].label)`.
//
// tMeta trả về CHÍNH object cũ khi không có gì phải dịch → không tạo tham chiếu
// mới mỗi lần render.

/** Khoá hợp lệ của một MessageDescriptor Lingui. */
const DESCRIPTOR_KEYS = new Set(['id', 'message', 'comment', 'values', 'context']);

/**
 * Có phải MessageDescriptor (`msg`…``) không?
 * Bản production babel strip `message`, chỉ còn `{ id: 'JKhSNT' }` — nên KHÔNG được
 * nhận diện bằng sự tồn tại của `message`. Nhận diện bằng: có `id` dạng chuỗi VÀ
 * mọi khoá còn lại đều thuộc bộ khoá descriptor. Nhờ vế sau mà dữ liệu thường có
 * trường `id` (vd `{ id: 'demo-u1', name: 'Huyền' }`) không bị nhận nhầm.
 */
function isMessageDescriptor(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v) || v.$$typeof) return false;
  if (typeof v.id !== 'string') return false;
  for (const k of Object.keys(v)) if (!DESCRIPTOR_KEYS.has(k)) return false;
  return true;
}

/**
 * Dịch mọi trường là MessageDescriptor trong một object cấu hình, giữ nguyên phần
 * còn lại (màu, class, Icon, key…). Chỉ soi 1 tầng — đúng tầng mà `msg`…`` được khai.
 *
 * @param {object|undefined|null} cfg
 * @returns {object|undefined|null} object đã dịch, hoặc chính `cfg` nếu không có gì để dịch
 */
/**
 * Dịch MỘT nhãn có thể là chuỗi thường HOẶC MessageDescriptor (`` msg`…` ``).
 *
 * Vì sao cần: cấu hình menu (NAV_GROUPS) của SaaS khai nhãn bằng `msg` để dịch được, còn Core
 * vẫn truyền chuỗi thường — sidebar là mã DÙNG CHUNG nên phải nuốt được cả hai. Trả nguyên
 * chuỗi khi không phải descriptor, nên bên nào chưa bọc vẫn chạy y như cũ.
 */
export function tLabel(v) {
  return isMessageDescriptor(v) ? i18n._(v) : v;
}

export function tMeta(cfg) {
  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return cfg;
  let out = null;
  for (const k of Object.keys(cfg)) {
    if (isMessageDescriptor(cfg[k])) {
      if (!out) out = { ...cfg };
      out[k] = i18n._(cfg[k]);
    }
  }
  return out || cfg;
}

// ── tOptions: hàng rào ĐẶT Ở COMPONENT RENDER, không ở chỗ gọi ───────────────
//
// ⚠️ Vì sao phải có, dù đã có tLabel/tMeta: bảng nhãn hay được ném NGUYÊN CỤM vào một
// renderer dùng chung — `<Select options={KIND_OPTIONS}/>`, `<AvatarSelect options={…}/>`,
// `filterFields` của DataTable. Bên trong renderer, `{opt.label}` là descriptor ⇒ React #31
// TRẮNG TRANG. Bọc ở CHỖ GỌI thì phải nhớ ở hàng chục nơi và mọi call-site tương lai; bọc ở
// renderer thì một lần là xong, kể cả code chưa viết. Đã sa 2 lần trong 17.08.2026:
// trang Lead (options của DataTable) và ô "Thời gian" ở báo cáo (presets của DateRangePill).
//
// Trả về CHÍNH mảng cũ khi không có gì phải dịch ⇒ không tạo tham chiếu mới mỗi lần render
// (giữ nguyên memo/deps của bên gọi).
export function tOptions(list) {
  if (!Array.isArray(list)) return list;
  let changed = false;
  const out = list.map((o) => {
    if (!o || typeof o !== 'object') return o;
    const label = tLabel(o.label);
    const nested = Array.isArray(o.options) ? tOptions(o.options) : o.options;
    if (label === o.label && nested === o.options) return o;
    changed = true;
    return Array.isArray(o.options) ? { ...o, label, options: nested } : { ...o, label };
  });
  return changed ? out : list;
}
